import { createGlobalStyle } from 'styled-components';

const GlobalStyles = createGlobalStyle`
  :root {
    --green-primary: #0E6B3C;
    --green-secondary: #6DBB1A;
    --gold-accent: #F2B705;
    --charcoal: #111827;
    --gray-soft: #F3F4F6;
    --gray-border: #E5E7EB;
    --white: #FFFFFF;

    --bg-primary: #FFFFFF;
    --bg-secondary: #F9FAFB;
    --bg-tertiary: #F3F4F6;
    --bg-section-alt: #F8FDF9;
    --bg-section-mint: #F0FAF4;
    --bg-hero: linear-gradient(165deg, #ffffff 0%, #f8fdf9 35%, #f0faf4 70%, #ffffff 100%);
    --bg-card: rgba(255, 255, 255, 0.92);
    --bg-glass: rgba(255, 255, 255, 0.78);

    --accent-emerald: #6DBB1A;

    --accent-primary: #0E6B3C;
    --accent-secondary: #6DBB1A;
    --accent-gold: #F2B705;

    --text-primary: #07111F;
    --text-dark: #07111F;
    --bg-surface: #F7FAFC;
    --text-secondary: #4B5563;
    --text-muted: #9CA3AF;

    --gradient-primary: linear-gradient(135deg, #0E6B3C 0%, #6DBB1A 100%);
    --gradient-text: linear-gradient(135deg, #0E6B3C 0%, #6DBB1A 70%, #0E6B3C 100%);
    --gradient-gold: linear-gradient(135deg, #F2B705 0%, #F59E0B 100%);
    --gradient-mesh:
      radial-gradient(ellipse 70% 50% at 10% 0%, rgba(109, 187, 26, 0.08) 0%, transparent 55%),
      radial-gradient(ellipse 50% 40% at 95% 10%, rgba(14, 107, 60, 0.06) 0%, transparent 50%);

    --border-subtle: rgba(14, 107, 60, 0.12);
    --border-glow: rgba(14, 107, 60, 0.28);
    --border-radius-sm: 10px;
    --border-radius-md: 16px;
    --border-radius-lg: 24px;
    --border-radius-xl: 32px;

    --shadow-sm: 0 1px 3px rgba(17, 24, 39, 0.06);
    --shadow-md: 0 8px 30px rgba(17, 24, 39, 0.08);
    --shadow-lg: 0 20px 50px rgba(14, 107, 60, 0.12);
    --shadow-glow: 0 12px 40px rgba(14, 107, 60, 0.18);

    --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    --font-display: 'Poppins', 'Inter', sans-serif;

    --container-width: 1320px;
    --container-padding: clamp(1rem, 4vw, 2rem);
    --nav-height: clamp(80px, 10vw, 96px);
    --header-offset: calc(var(--nav-height) + 24px);
    --radius-btn: 16px;
  }

  [data-theme='dark'] {
    --bg-primary: #0B1220;
    --bg-secondary: #111827;
    --bg-tertiary: #1F2937;
    --bg-card: rgba(17, 24, 39, 0.9);
    --bg-glass: rgba(17, 24, 39, 0.75);

    --text-primary: #F9FAFB;
    --text-secondary: #D1D5DB;
    --text-muted: #9CA3AF;

    --border-subtle: rgba(109, 187, 26, 0.15);
    --gray-soft: #1F2937;
    --gray-border: #374151;

    --shadow-md: 0 8px 30px rgba(0, 0, 0, 0.35);
    --shadow-lg: 0 20px 50px rgba(0, 0, 0, 0.4);

    --gradient-mesh:
      radial-gradient(ellipse 70% 50% at 10% 0%, rgba(109, 187, 26, 0.12) 0%, transparent 55%),
      radial-gradient(ellipse 50% 40% at 95% 10%, rgba(14, 107, 60, 0.1) 0%, transparent 50%);
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    scroll-behavior: smooth;
  }

  body {
    font-family: var(--font-body);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    line-height: 1.65;
    color: var(--text-primary);
    background: var(--bg-primary);
    overflow-x: hidden;
    transition: background 0.35s ease, color 0.35s ease;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: var(--font-display);
    font-weight: 700;
    letter-spacing: -0.025em;
  }

  .container {
    max-width: var(--container-width);
    margin: 0 auto;
    padding: 0 var(--container-padding);
    position: relative;
    z-index: 1;
  }

  main {
    min-height: 60vh;
    position: relative;
    z-index: 1;
  }

  .App {
    position: relative;
    min-height: 100vh;
    background: #fff;
  }

  .page-content {
    position: relative;
    z-index: 1;
  }

  .gradient-text {
    background: var(--gradient-text);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .btn-primary {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 14px 28px;
    border-radius: var(--radius-btn);
    background: var(--gradient-primary);
    color: #fff;
    font-family: var(--font-display);
    font-weight: 600;
    font-size: 0.95rem;
    text-decoration: none;
    border: none;
    cursor: pointer;
    box-shadow: var(--shadow-glow);
    transition: transform 0.25s ease, box-shadow 0.25s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 16px 40px rgba(14, 107, 60, 0.28);
    }
  }

  .btn-outline {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 14px 28px;
    border-radius: 12px;
    background: var(--bg-glass);
    border: 1px solid var(--border-subtle);
    color: var(--text-primary);
    font-family: var(--font-display);
    font-weight: 600;
    font-size: 0.95rem;
    text-decoration: none;
    backdrop-filter: blur(12px);
    transition: all 0.25s ease;

    &:hover {
      border-color: var(--accent-primary);
      transform: translateY(-2px);
      box-shadow: var(--shadow-md);
    }
  }

  @keyframes gradientFlow {
    0%, 100% { background-position: 0% center; }
    50% { background-position: 100% center; }
  }

  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(24px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-12px); }
  }

  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.05); opacity: 0.9; }
  }

  @media (max-width: 768px) {
    .container { padding: 0 16px; }
  }

  ::-webkit-scrollbar { width: 8px; }
  ::-webkit-scrollbar-track { background: var(--bg-secondary); }
  ::-webkit-scrollbar-thumb {
    background: var(--gradient-primary);
    border-radius: 4px;
  }

  ::selection {
    background: rgba(14, 107, 60, 0.2);
    color: var(--text-primary);
  }
`;

export default GlobalStyles;
